import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
const API = `${BACKEND_URL}/api`;

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState({ items: [], subtotal: 0, vat: 0, shipping: 0, total: 0 });
  const [settings, setSettings] = useState({});
  const [categories, setCategories] = useState([]); // Main categories with children
  const [allCategoriesFlat, setAllCategoriesFlat] = useState([]); // Flat list of all categories for lookups
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('session_id') || null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Global 401 interceptor — clears stale token and notifies user
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          // Only handle admin API 401s (not cart/public endpoints)
          if (url.includes('/api/') && !url.includes('/auth/') && !url.includes('/cart')) {
            const token = localStorage.getItem('token');
            if (token) {
              localStorage.removeItem('token');
              setUser(null);
              toast.error('Session expired — please log in again', { duration: 5000 });
            }
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const fetchCart = useCallback(async () => {
    try {
      const params = sessionId ? { session_id: sessionId } : {};
      const res = await axios.get(`${API}/cart`, { headers: getAuthHeaders(), params });
      setCart(res.data);
    } catch (e) {
      console.error('Failed to fetch cart', e);
      // Only toast if it's not a 401 (handled by checkAuth)
      if (e.response?.status !== 401) {
        toast.error('Failed to refresh your cart. Please check your connection.');
      }
    }
  }, [getAuthHeaders, sessionId]);

  const fetchSettings = useCallback(async () => {
    for (let attempt = 1; attempt <= 5; attempt++) {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
        return;
      } catch (e) {
        if (attempt < 5) {
          await new Promise(r => setTimeout(r, 2000));
        } else {
          console.error('Failed to fetch settings after retries', e);
        }
      }
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    // Retry up to 5 times with 2s delay — backend may still be starting in Docker
    for (let attempt = 1; attempt <= 5; attempt++) {
      try {
        const res = await axios.get(`${API}/categories`);
        const preferredOrder = [
          'Sanitaryware', 'Plumbing', 'Electrical', 'Lightings',
          'Safety', 'Tools', 'Power Tools', 'Hardware',
          'Paints', 'Automation', 'Bollards', 'Cabins'
        ];

        const rawCategories = res.data;

        // Build flat list for lookups using ALL categories (including stray ones)
        const flat = [];
        rawCategories.forEach(cat => {
          flat.push(cat);
          if (cat.children) {
            cat.children.forEach(child => flat.push(child));
          }
        });
        setAllCategoriesFlat(flat);

        // Sort the main categories based on preferredOrder.
        // Categories not in preferredOrder will be placed at the end.
        // Filter main categories to ONLY include those in preferredOrder
        // This prevents auto-created bulk upload categories from blowing up the sidebar length
        const mainCategories = rawCategories.filter(cat => preferredOrder.includes(cat.name));
        mainCategories.sort((a, b) => {
            const indexA = preferredOrder.indexOf(a.name);
            const indexB = preferredOrder.indexOf(b.name);
            return indexA - indexB;
        });

        // Sort children of each main category A-Z
        mainCategories.forEach(cat => {
          if (cat.children && Array.isArray(cat.children)) {
            cat.children.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          }
        });

        setCategories(mainCategories);
        return; // Success — exit retry loop
      } catch (e) {
        if (attempt < 5) {
          console.warn(`Categories fetch attempt ${attempt} failed. Retrying in 2s...`);
          await new Promise(r => setTimeout(r, 2000));
        } else {
          console.warn('Backend unavailable after retries. Categories could not be loaded.');
        }
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setUser(res.data);
      } catch (e) {
        localStorage.removeItem('token');
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      await Promise.all([checkAuth(), fetchSettings(), fetchCategories()]);
      await fetchCart();
    };
    init();
  }, [checkAuth, fetchSettings, fetchCategories, fetchCart]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data.requires_2fa) return res.data; // Handle admin 2FA flow
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    await fetchCart();
    return res.data;
  };

  const adminLogin = async (email, password) => {
    const res = await axios.post(`${API}/auth/admin-login`, { email, password });
    return res.data; // Will return requires_2fa, phone_choices, etc.
  };

  const adminSend2FA = async (pre_session_id, phone_index) => {
    const res = await axios.post(`${API}/auth/admin-send-2fa`, { pre_session_id, phone_index });
    return res.data; // Will return session_id for OTP verification
  };

  const adminVerify2FA = async (session_id, otp) => {
    const res = await axios.post(`${API}/auth/admin-verify-2fa`, { session_id, otp });
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };


  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    await fetchCart();
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setCart({ items: [], subtotal: 0, vat: 0, shipping: 0, total: 0 });
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setUser(res.data);
        return res.data;
      } catch (e) {
        console.error('Failed to refresh user', e);
      }
    }
    return null;
  };

  const addToCart = async (productId, quantity = 1) => {
    try {
      const params = sessionId ? { session_id: sessionId } : {};
      const res = await axios.post(`${API}/cart/add`, { product_id: productId, quantity }, { headers: getAuthHeaders(), params });
      if (res.data.session_id && !sessionId) {
        setSessionId(res.data.session_id);
        localStorage.setItem('session_id', res.data.session_id);
      }
      await fetchCart();
      toast.success('Added to cart');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add to cart');
    }
  };

  const updateCartItem = async (productId, quantity) => {
    try {
      const params = sessionId ? { session_id: sessionId } : {};
      await axios.put(`${API}/cart/update`, { product_id: productId, quantity }, { headers: getAuthHeaders(), params });
      await fetchCart();
    } catch (e) {
      toast.error('Failed to update cart');
    }
  };

  const removeFromCart = async (productId) => {
    try {
      const params = sessionId ? { session_id: sessionId } : {};
      await axios.delete(`${API}/cart/remove/${productId}`, { headers: getAuthHeaders(), params });
      await fetchCart();
      toast.success('Item removed');
    } catch (e) {
      toast.error('Failed to remove item');
    }
  };

  return (
    <AppContext.Provider value={{
      user, setUser, cart, setCart, settings, categories, allCategoriesFlat, loading,
      login, adminLogin, adminSend2FA, adminVerify2FA, register, logout, refreshUser, addToCart, updateCartItem, removeFromCart,
      fetchCart, fetchSettings, getAuthHeaders, sessionId, API
    }}>
      {children}
    </AppContext.Provider>
  );
};
